'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type AlgorithmType = 'token-bucket' | 'sliding-window' | 'fixed-window' | 'leaky-bucket';
type PricingTier = 'free' | 'basic' | 'pro' | 'enterprise';

interface RateLimitConfig {
  requestsPerSecond: number;
  burstCapacity: number;
  algorithm: AlgorithmType;
}

interface PricingTierConfig {
  tier: PricingTier;
  requestsPerMinute: number;
  burstAllowed: boolean;
  burstMultiplier: number;
}

const ALGORITHM_INFO: Record<AlgorithmType, { name: string; description: string; pros: string[]; cons: string[] }> = {
  'token-bucket': {
    name: 'Token Bucket',
    description: 'Tokens are added at a constant rate. Each request consumes a token. Allows bursts up to bucket capacity.',
    pros: [
      'Handles burst traffic well',
      'Simple to implement',
      'Smooth rate limiting',
      'Industry standard (AWS, Google)',
    ],
    cons: [
      'Requires tracking token state',
      'Memory overhead per user',
      'Clock synchronization needed',
    ],
  },
  'sliding-window': {
    name: 'Sliding Window Log',
    description: 'Tracks timestamp of each request in a sliding time window. Most accurate but memory intensive.',
    pros: [
      'Most accurate rate limiting',
      'No sudden rate resets',
      'Fair distribution',
      'Prevents gaming the system',
    ],
    cons: [
      'High memory usage',
      'Expensive to compute',
      'Scales poorly with high traffic',
    ],
  },
  'fixed-window': {
    name: 'Fixed Window Counter',
    description: 'Counts requests in fixed time windows (e.g., per minute). Resets at window boundary.',
    pros: [
      'Very simple to implement',
      'Low memory overhead',
      'Fast performance',
      'Easy to understand',
    ],
    cons: [
      'Allows burst at window edges',
      'Can exceed limits 2x at boundaries',
      'Not suitable for strict limits',
    ],
  },
  'leaky-bucket': {
    name: 'Leaky Bucket',
    description: 'Requests enter a queue and are processed at a constant rate. Smooths out bursts.',
    pros: [
      'Smooth constant output rate',
      'Simple implementation',
      'Good for downstream protection',
    ],
    cons: [
      'No burst handling',
      'Can add latency',
      'Queue can grow unbounded',
    ],
  },
};

export default function APIRateLimitPlannerPage() {
  // Basic Configuration
  const [expectedRPS, setExpectedRPS] = useState<number>(100);
  const [peakRPS, setPeakRPS] = useState<number>(500);
  const [avgResponseTimeMs, setAvgResponseTimeMs] = useState<number>(50);
  const [algorithm, setAlgorithm] = useState<AlgorithmType>('token-bucket');

  // Pricing Tiers
  const [pricingTiers, setPricingTiers] = useState<PricingTierConfig[]>([
    { tier: 'free', requestsPerMinute: 60, burstAllowed: false, burstMultiplier: 1 },
    { tier: 'basic', requestsPerMinute: 600, burstAllowed: true, burstMultiplier: 2 },
    { tier: 'pro', requestsPerMinute: 6000, burstAllowed: true, burstMultiplier: 3 },
    { tier: 'enterprise', requestsPerMinute: 60000, burstAllowed: true, burstMultiplier: 5 },
  ]);

  // Infrastructure
  const [serverCount, setServerCount] = useState<number>(3);
  const [cpuCoresPerServer, setCpuCoresPerServer] = useState<number>(4);
  const [redisMemoryMB, setRedisMemoryMB] = useState<number>(1024);

  // Command Center state
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'api-rate-limit-planner',
    toolCategory: 'planner',
  });

  // Calculations
  const calculations = useMemo(() => {
    const rpsPerServer = expectedRPS / serverCount;
    const peakRpsPerServer = peakRPS / serverCount;

    // Token bucket sizing
    const tokensPerSecond = expectedRPS;
    const bucketCapacity = peakRPS; // Allow peak traffic as burst
    const refillRate = tokensPerSecond;

    // Redis memory calculation (rough estimate)
    // Sliding window: ~100 bytes per request for 1 minute window
    const slidingWindowMemoryMB = (expectedRPS * 60 * 100) / (1024 * 1024);
    // Token bucket: ~50 bytes per user
    const estimatedUsers = 10000;
    const tokenBucketMemoryMB = (estimatedUsers * 50) / (1024 * 1024);

    // Fixed window: ~20 bytes per user (just counter + timestamp)
    const fixedWindowMemoryMB = (estimatedUsers * 20) / (1024 * 1024);

    // Memory needed based on algorithm
    const memoryNeeded = algorithm === 'sliding-window'
      ? slidingWindowMemoryMB
      : algorithm === 'token-bucket'
      ? tokenBucketMemoryMB
      : fixedWindowMemoryMB;

    // Cost per tier (simplified calculation)
    const costPerTier = pricingTiers.map((tier) => {
      const monthlyRequests = tier.requestsPerMinute * 60 * 24 * 30;
      const costPerMillion = tier.tier === 'free' ? 0 : tier.tier === 'basic' ? 1 : tier.tier === 'pro' ? 0.5 : 0.25;
      const monthlyCost = (monthlyRequests / 1_000_000) * costPerMillion;
      return {
        tier: tier.tier,
        monthlyRequests,
        monthlyCost,
        dailyRequests: tier.requestsPerMinute * 60 * 24,
      };
    });

    // DDoS protection threshold (100x normal traffic)
    const ddosThreshold = expectedRPS * 100;
    const ddosProtectionNeeded = peakRPS > (expectedRPS * 10);

    // Infrastructure capacity
    const maxRequestsPerCore = 1000; // Rough estimate
    const totalCapacity = serverCount * cpuCoresPerServer * maxRequestsPerCore;
    const utilizationPercent = (expectedRPS / totalCapacity) * 100;
    const peakUtilizationPercent = (peakRPS / totalCapacity) * 100;

    return {
      rpsPerServer,
      peakRpsPerServer,
      tokensPerSecond,
      bucketCapacity,
      refillRate,
      memoryNeeded,
      redisMemorySufficient: redisMemoryMB >= memoryNeeded,
      costPerTier,
      ddosThreshold,
      ddosProtectionNeeded,
      totalCapacity,
      utilizationPercent,
      peakUtilizationPercent,
      capacityWarning: peakUtilizationPercent > 80,
    };
  }, [expectedRPS, peakRPS, algorithm, serverCount, cpuCoresPerServer, redisMemoryMB, pricingTiers]);

  // Update pricing tier
  const updateTier = (tier: PricingTier, field: keyof PricingTierConfig, value: number | boolean) => {
    setPricingTiers(pricingTiers.map((t) =>
      t.tier === tier ? { ...t, [field]: value } : t
    ));
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Configuration Summary
      const summaryData = [
        ['API Rate Limit Planner', ''],
        ['', ''],
        ['Configuration', ''],
        ['Expected RPS', expectedRPS],
        ['Peak RPS', peakRPS],
        ['Avg Response Time (ms)', avgResponseTimeMs],
        ['Algorithm', ALGORITHM_INFO[algorithm].name],
        ['', ''],
        ['Infrastructure', ''],
        ['Server Count', serverCount],
        ['CPU Cores per Server', cpuCoresPerServer],
        ['Total Capacity (RPS)', calculations.totalCapacity],
        ['Utilization (%)', calculations.utilizationPercent.toFixed(1) + '%'],
        ['Peak Utilization (%)', calculations.peakUtilizationPercent.toFixed(1) + '%'],
        ['', ''],
        ['Redis Configuration', ''],
        ['Memory Allocated (MB)', redisMemoryMB],
        ['Memory Needed (MB)', calculations.memoryNeeded.toFixed(2)],
        ['Sufficient?', calculations.redisMemorySufficient ? 'Yes' : 'No'],
        ['', ''],
        ['Rate Limiting', ''],
        ['Tokens per Second', calculations.tokensPerSecond],
        ['Bucket Capacity', calculations.bucketCapacity],
        ['Refill Rate', calculations.refillRate],
        ['', ''],
        ['DDoS Protection', ''],
        ['DDoS Threshold (RPS)', calculations.ddosThreshold],
        ['Protection Needed?', calculations.ddosProtectionNeeded ? 'Yes' : 'No'],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Pricing Tiers
      const tierData = calculations.costPerTier.map((t) => ({
        'Tier': t.tier.charAt(0).toUpperCase() + t.tier.slice(1),
        'Requests/Min': pricingTiers.find(pt => pt.tier === t.tier)?.requestsPerMinute,
        'Daily Requests': t.dailyRequests.toLocaleString(),
        'Monthly Requests': t.monthlyRequests.toLocaleString(),
        'Monthly Cost': '$' + t.monthlyCost.toFixed(2),
      }));

      const ws2 = XLSX.utils.json_to_sheet(tierData);
      ws2['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Pricing Tiers');

      // Sheet 3: Algorithm Comparison
      const algoData = Object.entries(ALGORITHM_INFO).map(([key, info]) => ({
        'Algorithm': info.name,
        'Best For': info.pros[0],
        'Pros': info.pros.join('; '),
        'Cons': info.cons.join('; '),
        'Selected': key === algorithm ? 'Yes' : 'No',
      }));

      const ws3 = XLSX.utils.json_to_sheet(algoData);
      ws3['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 50 }, { wch: 50 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Algorithms');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'api-rate-limit-plan.xlsx');
    };
    document.head.appendChild(script);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const tierScores: Record<string, number> = {};
    pricingTiers.forEach(tier => {
      tierScores[tier.tier] = tier.requestsPerMinute;
    });

    const recommendations = [];
    if (calculations.capacityWarning) {
      recommendations.push({
        id: 'capacity-warning',
        title: 'Increase Server Capacity',
        priority: 'high' as const,
        description: `Peak utilization at ${calculations.peakUtilizationPercent.toFixed(1)}% exceeds 80%. Consider adding more servers.`,
      });
    }
    if (!calculations.redisMemorySufficient) {
      recommendations.push({
        id: 'redis-memory',
        title: 'Increase Redis Memory',
        priority: 'high' as const,
        description: `Redis memory (${redisMemoryMB}MB) is insufficient. Need at least ${calculations.memoryNeeded.toFixed(2)}MB.`,
      });
    }
    if (calculations.ddosProtectionNeeded) {
      recommendations.push({
        id: 'ddos-protection',
        title: 'Implement DDoS Protection',
        priority: 'medium' as const,
        description: 'Peak traffic significantly exceeds normal. Implement CloudFlare or AWS WAF for L7 DDoS protection.',
      });
    }
    recommendations.push({
      id: 'algorithm-selected',
      title: `Using ${ALGORITHM_INFO[algorithm].name}`,
      priority: 'low' as const,
      description: ALGORITHM_INFO[algorithm].description,
    });

    const assessment = await saveResult({
      name: `API Rate Limit Plan - ${new Date().toLocaleDateString()}`,
      score_overall: Math.min(100, Math.max(0,
        (calculations.redisMemorySufficient ? 40 : 0) +
        (calculations.capacityWarning ? 0 : 40) +
        (!calculations.ddosProtectionNeeded ? 20 : 10)
      )),
      scores: tierScores,
      answers: {
        expectedRPS,
        peakRPS,
        avgResponseTimeMs,
        algorithm,
        serverCount,
        cpuCoresPerServer,
        redisMemoryMB,
        pricingTiers,
      },
      recommendations,
      metadata: {
        tokensPerSecond: calculations.tokensPerSecond,
        bucketCapacity: calculations.bucketCapacity,
        totalCapacity: calculations.totalCapacity,
        utilizationPercent: calculations.utilizationPercent,
        peakUtilizationPercent: calculations.peakUtilizationPercent,
        memoryNeeded: calculations.memoryNeeded,
        ddosThreshold: calculations.ddosThreshold,
        algorithmName: ALGORITHM_INFO[algorithm].name,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            API Rate Limit Planner
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Calculate optimal rate limits, compare algorithms, and plan infrastructure capacity.
            Design multi-tier pricing with burst handling and DDoS protection.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Traffic Configuration */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Traffic Configuration
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Expected RPS (Requests per Second)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={expectedRPS}
                    onChange={(e) => setExpectedRPS(Math.max(1, parseInt(e.target.value) || 0))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Normal traffic load
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Peak RPS (Peak Load)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={peakRPS}
                    onChange={(e) => setPeakRPS(Math.max(1, parseInt(e.target.value) || 0))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Maximum expected burst traffic
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Avg Response Time (ms)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={avgResponseTimeMs}
                    onChange={(e) => setAvgResponseTimeMs(Math.max(1, parseInt(e.target.value) || 0))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Average API response time
                  </p>
                </div>
              </div>
            </div>

            {/* Infrastructure */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Infrastructure
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Server Count
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={serverCount}
                    onChange={(e) => setServerCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    CPU Cores per Server
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={cpuCoresPerServer}
                    onChange={(e) => setCpuCoresPerServer(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Redis Memory (MB)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={redisMemoryMB}
                    onChange={(e) => setRedisMemoryMB(Math.max(64, parseInt(e.target.value) || 64))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Memory for rate limit storage
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-300 dark:border-border">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Total Capacity</div>
                      <div className="text-lg font-bold text-light-text dark:text-foreground">
                        {calculations.totalCapacity.toLocaleString()} RPS
                      </div>
                    </div>
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Utilization</div>
                      <div className={`text-lg font-bold ${
                        calculations.peakUtilizationPercent > 80
                          ? 'text-red-500'
                          : calculations.peakUtilizationPercent > 60
                          ? 'text-yellow-500'
                          : 'text-green-500'
                      }`}>
                        {calculations.peakUtilizationPercent.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>

                {calculations.capacityWarning && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm text-red-500 font-semibold">
                      ⚠️ Warning: Peak utilization exceeds 80%. Consider adding more servers.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Algorithm Selection */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Rate Limiting Algorithm
              </h2>

              <div className="space-y-3">
                {Object.entries(ALGORITHM_INFO).map(([key, info]) => (
                  <div
                    key={key}
                    onClick={() => setAlgorithm(key as AlgorithmType)}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      algorithm === key
                        ? 'border-accent bg-accent-muted'
                        : 'border-gray-300 dark:border-border hover:border-border-muted0'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="radio"
                        checked={algorithm === key}
                        onChange={() => setAlgorithm(key as AlgorithmType)}
                        className="w-4 h-4"
                      />
                      <div className="font-semibold text-light-text dark:text-foreground">{info.name}</div>
                    </div>
                    <p className="text-sm text-light-text-secondary dark:text-foreground-secondary ml-7">
                      {info.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Rate Limit Configuration */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Recommended Configuration
              </h2>

              <div className="space-y-4">
                <div className="bg-accent-muted border border-border rounded-lg p-4">
                  <div className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">
                    Algorithm
                  </div>
                  <div className="text-xl font-bold text-accent">
                    {ALGORITHM_INFO[algorithm].name}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                    <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                      Rate (tokens/sec)
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-foreground">
                      {calculations.tokensPerSecond}
                    </div>
                  </div>

                  <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                    <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                      Burst Capacity
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-foreground">
                      {calculations.bucketCapacity}
                    </div>
                  </div>

                  <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                    <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                      RPS per Server
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-foreground">
                      {calculations.rpsPerServer.toFixed(0)}
                    </div>
                  </div>

                  <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                    <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                      Peak per Server
                    </div>
                    <div className="text-2xl font-bold text-light-text dark:text-foreground">
                      {calculations.peakRpsPerServer.toFixed(0)}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-300 dark:border-border">
                  <div className="text-sm font-semibold text-light-text dark:text-foreground mb-2">
                    Redis Memory
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Allocated</span>
                    <span className="text-sm font-semibold text-light-text dark:text-foreground">
                      {redisMemoryMB} MB
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Needed</span>
                    <span className="text-sm font-semibold text-light-text dark:text-foreground">
                      {calculations.memoryNeeded.toFixed(2)} MB
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Status</span>
                    <span className={`text-sm font-semibold ${
                      calculations.redisMemorySufficient ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {calculations.redisMemorySufficient ? '✓ Sufficient' : '⚠ Insufficient'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Tiers */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Pricing Tiers
              </h2>

              <div className="space-y-3">
                {pricingTiers.map((tier) => {
                  const cost = calculations.costPerTier.find(t => t.tier === tier.tier);
                  return (
                    <div key={tier.tier} className="border border-gray-300 dark:border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-bold text-light-text dark:text-foreground capitalize">
                            {tier.tier}
                          </div>
                          <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                            {tier.requestsPerMinute.toLocaleString()} req/min
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-accent">
                            ${cost?.monthlyCost.toFixed(2)}
                          </div>
                          <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">per month</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-light-text-secondary dark:text-foreground-tertiary">Daily: </span>
                          <span className="text-light-text dark:text-foreground font-semibold">
                            {cost?.dailyRequests.toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-light-text-secondary dark:text-foreground-tertiary">Monthly: </span>
                          <span className="text-light-text dark:text-foreground font-semibold">
                            {cost?.monthlyRequests.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {tier.burstAllowed && (
                        <div className="mt-2 text-xs text-accent">
                          ⚡ Burst: {tier.burstMultiplier}x allowed
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* DDoS Protection */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                DDoS Protection
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Normal Traffic</span>
                  <span className="text-lg font-bold text-light-text dark:text-foreground">
                    {expectedRPS} RPS
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">DDoS Threshold (100x)</span>
                  <span className="text-lg font-bold text-red-500">
                    {calculations.ddosThreshold.toLocaleString()} RPS
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Protection Needed?</span>
                  <span className={`text-lg font-bold ${
                    calculations.ddosProtectionNeeded ? 'text-accent' : 'text-green-500'
                  }`}>
                    {calculations.ddosProtectionNeeded ? 'Yes' : 'No'}
                  </span>
                </div>

                {calculations.ddosProtectionNeeded && (
                  <div className="bg-accent-muted border border-border rounded-lg p-3 mt-3">
                    <div className="text-sm font-semibold text-accent mb-2">
                      Recommendations:
                    </div>
                    <ul className="text-xs text-light-text-secondary dark:text-foreground-secondary space-y-1">
                      <li>• Use CloudFlare or AWS WAF for L7 DDoS protection</li>
                      <li>• Implement IP-based rate limiting</li>
                      <li>• Add CAPTCHA for suspicious traffic</li>
                      <li>• Set up geo-blocking if needed</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Algorithm Details */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Selected Algorithm Details
              </h2>

              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-light-text dark:text-foreground mb-2">
                    {ALGORITHM_INFO[algorithm].name}
                  </div>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                    {ALGORITHM_INFO[algorithm].description}
                  </p>
                </div>

                <div>
                  <div className="text-sm font-semibold text-green-500 mb-2">Pros:</div>
                  <ul className="space-y-1">
                    {ALGORITHM_INFO[algorithm].pros.map((pro, idx) => (
                      <li key={idx} className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                        ✓ {pro}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-sm font-semibold text-red-500 mb-2">Cons:</div>
                  <ul className="space-y-1">
                    {ALGORITHM_INFO[algorithm].cons.map((con, idx) => (
                      <li key={idx} className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                        ✗ {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Export */}
            <button
              onClick={exportToExcel}
              className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              📊 Export to Excel
            </button>

            {/* Command Center Save Button */}
            <div className="mt-4 flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Multi-tier pricing calculator</li>
                <li>✓ DDoS protection analysis</li>
                <li>✓ Redis sizing recommendations</li>
                <li>✓ Cost analysis with graphs</li>
                <li>✓ Excel export with formulas</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Multi-region distribution</li>
                <li>✓ GraphQL cost analysis</li>
                <li>✓ Enterprise gateway configs</li>
                <li>✓ Custom algorithm simulation</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
